import { useDispatch } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useConfig from "../../../actions/Tenants/config/configHook";
import { callAvosApi } from "../../../utils/useAvosApiHook";
import { toast } from "../../../utils/toast";
import { getAllTenantFieldOptions } from "../../../actions/Tenants/tenantFieldOptionsActions";
import { useImport } from "./useImport";
import { useExport } from "./useExport";
import { UseImportExportProps, UseImportExportReturn } from "./types";

export const useImportExport = ({ entityName, customEndpoint, importFields, affectedQueries = [], excludeFields = [], uniqueIdentifierKey = "name", validateImportItems }: UseImportExportProps): UseImportExportReturn => {
    const dispatch = useDispatch();
    const config = useConfig();
    const queryClient = useQueryClient();

    const queryKey = `all-${entityName}s`;
    const endpoint = customEndpoint || `/${entityName}/`;

    const { onImportData, setImportData, onDownloadTemplate, validItems, errors } = useImport({ entityName, importFields, affectedQueries, customEndpoint, uniqueIdentifierKey, validateImportItems });
    const { onExportData } = useExport({ entityName, importFields, customEndpoint, excludeFields });

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: [queryKey], exact: false });
        queryClient.invalidateQueries({ queryKey: [`${entityName}s`], exact: false });
        affectedQueries.forEach((q) => {
            queryClient.invalidateQueries({ queryKey: [q], exact: false });
        });
    };

    const { mutate: deleteAll } = useMutation({
        mutationFn: async () => {
            return callAvosApi(`${endpoint}all`, {
                method: "DELETE",
            });
        },
        onSuccess: () => {
            toast.success(`All ${entityName}s deleted successfully.`);
            invalidateQueries();
            dispatch(getAllTenantFieldOptions(config.tenant_id) as any);

        },
        onError: (error: any) => {
            toast.error(error?.message || `An error occurred while deleting all ${entityName}s.`);
        }
    });

    const onDeleteAll = async () => {
        await deleteAll();
        dispatch(getAllTenantFieldOptions(config.tenant_id) as any);
    };

    return {
        onImportData,
        onFileChange: setImportData,
        validItems,
        errors,
        onExportData,
        onDownloadTemplate,
        onDeleteAll,
    };
};
